// The decision. Three ways to dispose of a finding, all reversible, and one of
// them — "Not a concern" — refuses to commit until it knows why.
//
// A dismissal with no reason recorded is a decision that cannot be defended
// three months later, and it is also what would make the feedback loop in step
// 14 impossible: a check nobody can say why they keep setting aside cannot be
// quieted safely. So the reason is a precondition, not a prompt. The state
// machine behind this is in src/components/finding/disposition.ts.

import { useId, useReducer, useState } from 'react';

import { Button, Label } from '../primitives';
import { DISMISS_REASONS, describeDisposition } from '../../data/review';
import {
  canCommit,
  dispositionReducer,
  emptyDraft,
  toDisposition,
  type DispositionDraft,
} from './disposition';
import type { Disposition } from '../../types';
import './DispositionControl.css';

export interface DispositionControlProps {
  findingId: string;
  /** The decision on record, or null while the finding is undecided. */
  disposition: Disposition | null;
  /** Called with the new record, or with null to take the decision back. */
  onDispositionChange(next: Disposition | null): void;
  /** The comment as it stands: the reviewer's edit, or the generated draft. */
  comment: string;
  /** A new comment, or null to go back to the generated draft. */
  onCommentChange(next: string | null): void;
  /** True once the reviewer has edited the comment, so it can be reverted. */
  commentEdited?: boolean;
  /**
   * Where the control starts. Defaults to nothing entered; set when a draft is
   * being restored, and how a check harness renders a mid-decision state
   * without a browser to click in.
   */
  initialDraft?: DispositionDraft;
}

export default function DispositionControl({
  findingId,
  disposition,
  onDispositionChange,
  comment,
  onCommentChange,
  commentEdited = false,
  initialDraft,
}: DispositionControlProps) {
  const [draft, dispatch] = useReducer(
    dispositionReducer,
    initialDraft ?? emptyDraft(),
  );
  const [editingComment, setEditingComment] = useState(false);
  const [commentText, setCommentText] = useState(comment);
  const ids = useId();

  const decided = disposition !== null && !draft.editing;

  const commit = (decision: Disposition['decision']) => {
    const next = toDisposition(draft, decision, findingId);
    if (!next) return;
    dispatch({ type: 'committed' });
    onDispositionChange(next);
  };

  /* ---------------- a decision is on record ---------------- */

  if (decided) {
    const included = disposition.decision === 'include';
    return (
      <div className="disp disp--decided">
        <p className="disp__decided-line">
          <span className="disp__mark" aria-hidden="true">
            ◆
          </span>
          {included ? (
            <span>Added to the comment sheet.</span>
          ) : (
            <span>
              <span className="disp__decided-label">Decided:</span>{' '}
              {describeDisposition(disposition)}
            </span>
          )}
          {included ? (
            <Button
              variant="ghost"
              aria-pressed={editingComment}
              onClick={() => {
                setCommentText(comment);
                setEditingComment((v) => !v);
              }}
            >
              {editingComment ? 'Close the comment' : 'Edit the comment'}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            onClick={() => dispatch({ type: 'edit', from: disposition })}
          >
            Change
          </Button>
        </p>

        {disposition.note ? (
          <p className="disp__note-shown">
            <Label>Your note</Label>
            <span>{disposition.note}</span>
          </p>
        ) : null}

        {included && editingComment ? (
          <div className="disp__panel">
            <label className="disp__field" htmlFor={`${ids}-comment`}>
              <Label>The comment that goes on the sheet</Label>
              <textarea
                id={`${ids}-comment`}
                className="disp__textarea"
                rows={6}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </label>
            <div className="disp__row">
              <Button
                variant="primary"
                onClick={() => {
                  onCommentChange(commentText);
                  setEditingComment(false);
                }}
              >
                Save the comment
              </Button>
              {commentEdited ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    onCommentChange(null);
                    setEditingComment(false);
                  }}
                >
                  Back to the draft
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => setEditingComment(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  /* ---------------- undecided, or a decision being changed ---------------- */

  return (
    <div className="disp">
      <div
        className="disp__row"
        role="group"
        aria-label="What to do with this finding"
      >
        {/* "Include as comment" is an action and commits on the click. The
            other two choose a path and are toggles: the panel that follows is
            the rest of the same decision, not a disclosure of something that
            was already decided, so they carry aria-pressed rather than
            aria-expanded. */}
        <Button variant="primary" onClick={() => commit('include')}>
          Include as comment
        </Button>
        <Button
          variant="secondary"
          aria-pressed={draft.panel === 'not-a-concern'}
          onClick={() => dispatch({ type: 'open', panel: 'not-a-concern' })}
        >
          Not a concern
        </Button>
        <Button
          variant="secondary"
          aria-pressed={draft.panel === 'refer'}
          onClick={() => dispatch({ type: 'open', panel: 'refer' })}
        >
          Ask an SME
        </Button>
        {/* Reversing a decision entirely, rather than replacing it with
            another one. */}
        {disposition !== null ? (
          <Button
            variant="ghost"
            onClick={() => {
              dispatch({ type: 'committed' });
              onDispositionChange(null);
            }}
          >
            Undecide this
          </Button>
        ) : null}
      </div>

      {draft.panel === 'not-a-concern' ? (
        <div className="disp__panel" role="group" aria-label="Set this finding aside">
          <fieldset className="disp__reasons">
            <legend className="disp__legend">
              <Label>Why is it not a concern?</Label>
            </legend>
            {DISMISS_REASONS.map((reason) => (
              <label key={reason.value} className="disp__reason">
                <input
                  type="radio"
                  name={`${ids}-reason`}
                  value={reason.value}
                  checked={draft.reason === reason.value}
                  onChange={() =>
                    dispatch({ type: 'reason', reason: reason.value })
                  }
                />
                <span>{reason.label}</span>
              </label>
            ))}
          </fieldset>

          {draft.reason === 'other' ? (
            <label className="disp__field" htmlFor={`${ids}-reason-text`}>
              <Label>What was it?</Label>
              <input
                id={`${ids}-reason-text`}
                className="disp__input"
                type="text"
                value={draft.reasonText}
                onChange={(e) =>
                  dispatch({ type: 'reason-text', value: e.target.value })
                }
              />
            </label>
          ) : null}

          <NoteField
            id={`${ids}-note`}
            value={draft.note}
            onChange={(value) => dispatch({ type: 'note', value })}
          />

          <div className="disp__row">
            <Button
              variant="primary"
              disabled={!canCommit(draft)}
              onClick={() => commit('not-a-concern')}
            >
              Set aside
            </Button>
            <Button variant="ghost" onClick={() => dispatch({ type: 'cancel' })}>
              Cancel
            </Button>
            {canCommit(draft) ? null : (
              <span className="disp__requirement">
                Pick a reason first. It is recorded with the decision.
              </span>
            )}
          </div>
        </div>
      ) : null}

      {draft.panel === 'refer' ? (
        <div className="disp__panel" role="group" aria-label="Ask an SME about this finding">
          <label className="disp__field" htmlFor={`${ids}-sme`}>
            <Label>Who are you asking? A name or a role — optional</Label>
            <input
              id={`${ids}-sme`}
              className="disp__input"
              type="text"
              value={draft.referredTo}
              onChange={(e) =>
                dispatch({ type: 'referred-to', value: e.target.value })
              }
            />
          </label>

          <NoteField
            id={`${ids}-refer-note`}
            value={draft.note}
            onChange={(value) => dispatch({ type: 'note', value })}
          />

          <div className="disp__row">
            <Button variant="primary" onClick={() => commit('refer')}>
              Ask an SME
            </Button>
            <Button variant="ghost" onClick={() => dispatch({ type: 'cancel' })}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The note offered on every panel, and required by none of them. */
function NoteField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange(value: string): void;
}) {
  return (
    <label className="disp__field" htmlFor={id}>
      <Label>Your note — optional</Label>
      <textarea
        id={id}
        className="disp__textarea"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
